"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Bitcoin = void 0;
const axios_1 = __importDefault(require("axios"));
const bitcoin = __importStar(require("bitcoinjs-lib"));
const signature_1 = require("../../signature");
const utils_1 = require("./utils");
class Bitcoin {
    constructor(config) {
        this.network = config.network;
        this.providerUrl = config.providerUrl;
        this.relayerUrl = config.relayerUrl;
        this.contract = config.contract;
    }
    /**
     * Converts a value from satoshis to bitcoins.
     *
     * @param {number} satoshis - The amount in satoshis to convert.
     * @returns {number} The equivalent amount in bitcoins.
     */
    static toBTC(satoshis) {
        return satoshis / 100000000;
    }
    /**
     * Converts a value from bitcoins to satoshis.
     *
     * @param {number} btc - The amount in bitcoins to convert.
     * @returns {number} The equivalent amount in satoshis.
     */
    static toSatoshi(btc) {
        return btc * 100000000;
    }
    /**
     * Fetches the balance for a given Bitcoin address.
     * This function retrieves all unspent transaction outputs (UTXOs) for the address,
     * sums their values to calculate the total balance, and returns it as a string.
     *
     * @param {string} address - The Bitcoin address for which to fetch the balance.
     * @returns {Promise<string>} A promise that resolves to the balance of the address as a string.
     */
    fetchBalance(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const utxos = yield (0, utils_1.fetchBTCUTXOs)(this.providerUrl, address);
            return Bitcoin.toBTC(utxos.reduce((acc, utxo) => acc + utxo.value, 0)).toString();
        });
    }
    /**
     * Fetches a Bitcoin transaction by its ID and constructs a transaction object.
     * This function retrieves the transaction details from the blockchain using the RPC endpoint,
     * then parses the input and output data to construct a `bitcoin.Transaction` object.
     *
     * @param {string} transactionId - The ID of the transaction to fetch.
     * @returns {Promise<bitcoin.Transaction>} A promise that resolves to a `bitcoin.Transaction` object representing the fetched transaction.
     */
    fetchTransaction(transactionId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield axios_1.default.get(`${this.providerUrl}tx/${transactionId}`);
            const tx = new bitcoin.Transaction();
            tx.version = data.version;
            tx.locktime = data.locktime;
            data.vin.forEach((vin) => {
                const txHash = Buffer.from(vin.txid, 'hex').reverse();
                const { vout } = vin;
                const { sequence } = vin;
                const scriptSig = vin.scriptsig
                    ? Buffer.from(vin.scriptsig, 'hex')
                    : undefined;
                tx.addInput(txHash, vout, sequence, scriptSig);
            });
            data.vout.forEach((vout) => {
                const { value } = vout;
                const scriptPubKey = Buffer.from(vout.scriptpubkey, 'hex');
                tx.addOutput(scriptPubKey, value);
            });
            data.vin.forEach((vin, index) => {
                if (vin.witness && vin.witness.length > 0) {
                    const witness = vin.witness.map((w) => Buffer.from(w, 'hex'));
                    tx.setWitness(index, witness);
                }
            });
            return tx;
        });
    }
    /**
     * This function takes an object containing the r and s components of a signature,
     * pads them to ensure they are each 64 characters long, concatenates them,
     * and then converts the concatenated string into a Buffer. This Buffer represents
     * the full signature in hexadecimal format. If the resulting Buffer is not exactly
     * 64 bytes long, an error is thrown.
     *
     * @param {Object} signature - An object containing the r and s components of a signature.
     * @param {string} signature.r - The r component of the signature.
     * @param {string} signature.s - The s component of the signature.
     * @returns {Buffer} A Buffer representing the concatenated r and s components of the signature.
     * @throws {Error} Throws an error if the resulting Buffer is not exactly 64 bytes long.
     */
    static joinSignature(signature) {
        const r = signature.r.padStart(64, '0');
        const s = signature.s.padStart(64, '0');
        const rawSignature = Buffer.from(r + s, 'hex');
        if (rawSignature.length !== 64) {
            throw new Error('Invalid signature length.');
        }
        return rawSignature;
    }
    /**
     * Sends a signed transaction to the Bitcoin network.
     * This function takes the hexadecimal representation of a signed transaction
     * and broadcasts it to the network using a proxy URL to bypass CORS restrictions.
     * It logs the transaction ID if the broadcast is successful, or an error message otherwise.
     *
     * @param {string} txHex - The hexadecimal string of the signed transaction.
     * @param {Object} [options] - Optional parameters.
     * @param {boolean} [options.proxy=false] - Whether to use a proxy URL for the transaction broadcast.
     * @returns {Promise<string>} A promise that resolves with the txid once the transaction is successfully broadcast
     */
    sendTransaction(txHex, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const proxyUrl = (options === null || options === void 0 ? void 0 : options.proxy) ? 'https://corsproxy.io/?' : '';
                const response = yield axios_1.default.post(`${proxyUrl}${this.providerUrl}tx`, txHex);
                if (response.status === 200) {
                    return response.data;
                }
                throw new Error(`Failed to broadcast transaction: ${response.data}`);
            }
            catch (error) {
                throw new Error(`Error broadcasting transaction: ${error}`);
            }
        });
    }
    /**
     * Handles the process of creating and broadcasting a Bitcoin transaction.
     * This function takes the recipient's address, the amount to send, the account details,
     * and the derived path for the account to create a transaction. It then signs the transaction
     * using the chain signature contract and broadcasts it to the Bitcoin network.
     *
     * @param {BTCTransaction} data - The transaction data.
     * @param {string} data.to - The recipient's Bitcoin address.
     * @param {string} data.value - The amount of Bitcoin to send (in BTC).
     * @param {NearAuthentication} nearAuthentication - The object containing the user's authentication information.
     * @param {string} path - The key derivation path for the account.
     */
    handleTransaction(data, nearAuthentication, path) {
        return __awaiter(this, void 0, void 0, function* () {
            const { address, publicKey } = yield (0, utils_1.fetchDerivedBTCAddressAndPublicKey)({
                signerId: nearAuthentication.accountId,
                path,
                btcNetworkId: this.network,
                nearNetworkId: nearAuthentication.networkId,
                multichainContractId: this.contract,
            });
            const { inputs, outputs } = data.inputs && data.outputs
                ? data
                : yield (0, utils_1.fetchBTCFeeProperties)(this.providerUrl, address, [
                    {
                        address: data.to,
                        value: parseFloat(data.value),
                    },
                ]);
            const psbt = new bitcoin.Psbt({
                network: (0, utils_1.parseBTCNetwork)(this.network),
            });
            yield Promise.all(inputs.map((utxo) => __awaiter(this, void 0, void 0, function* () {
                const transaction = yield this.fetchTransaction(utxo.txid);
                let inputOptions;
                if (transaction.outs[utxo.vout].script.includes('0014')) {
                    inputOptions = {
                        hash: utxo.txid,
                        index: utxo.vout,
                        witnessUtxo: {
                            script: transaction.outs[utxo.vout].script,
                            value: utxo.value,
                        },
                    };
                }
                else {
                    inputOptions = {
                        hash: utxo.txid,
                        index: utxo.vout,
                        nonWitnessUtxo: Buffer.from(transaction.toHex(), 'hex'),
                    };
                }
                psbt.addInput(inputOptions);
            })));
            outputs.forEach((out) => {
                if (!out.address) {
                    out.address = address;
                }
                psbt.addOutput({
                    address: out.address,
                    value: out.value,
                });
            });
            const mpcKeyPair = {
                publicKey,
                sign: (transactionHash) => __awaiter(this, void 0, void 0, function* () {
                    const signature = yield (0, signature_1.sign)({
                        transactionHash,
                        path,
                        nearAuthentication,
                        contract: this.contract,
                        relayerUrl: this.relayerUrl,
                    });
                    if (!signature) {
                        throw new Error('Failed to sign transaction');
                    }
                    return Buffer.from(Bitcoin.joinSignature(signature));
                }),
            };
            // TODO: it should be done in parallel,
            // but for now it's causing nonce issues on the signDelegate so we will run sequentially to avoid the issue for now
            for (let index = 0; index < inputs.length; index += 1) {
                // eslint-disable-next-line no-await-in-loop
                yield psbt.signInputAsync(index, mpcKeyPair);
            }
            psbt.finalizeAllInputs();
            const txid = yield this.sendTransaction(psbt.extractTransaction().toHex(), {
                proxy: true,
            });
            if (txid) {
                return txid;
            }
            return 'Error';
        });
    }
}
exports.Bitcoin = Bitcoin;
